import type { LoaderFunctionArgs } from "react-router-dom";
import { redirect } from "react-router-dom";
import { networkAPI, tierAPI, membershipAPI, networkRevenueAPI, APIError } from "../../lib/api";
import type {
  NetworkResponse,
  NetworkTierResponse,
  NetworkMembershipResponse,
} from "../../types/api";
import type { RevenueSummary } from "../../types/networkAnalytics";

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isValidUUID(value: string): boolean {
  return UUID_REGEX.test(value);
}

export interface NetworkListData {
  networks: NetworkResponse[];
  organizationId: string;
}

export interface NetworkDetailData {
  network: NetworkResponse;
  tiers: NetworkTierResponse[];
  organizationId: string;
}

export interface NetworkMembershipsData {
  network: NetworkResponse;
  tiers: NetworkTierResponse[];
  memberships: NetworkMembershipResponse[];
  organizationId: string;
}

export interface CreateNetworkLoaderData {
  organizationId: string;
}

export async function networkListLoader({ params }: LoaderFunctionArgs): Promise<NetworkListData> {
  const { id: orgId } = params;

  if (!orgId || !isValidUUID(orgId)) {
    throw redirect("/organizations");
  }

  try {
    const networks = await networkAPI.getAll(orgId);
    return { networks, organizationId: orgId };
  } catch (error) {
    if (error instanceof APIError && error.statusCode === 404) {
      throw redirect("/organizations");
    }
    throw error;
  }
}

export async function networkDetailLoader({
  params,
}: LoaderFunctionArgs): Promise<NetworkDetailData> {
  const { id: orgId, networkId } = params;

  if (!orgId || !networkId || !isValidUUID(orgId) || !isValidUUID(networkId)) {
    throw redirect("/organizations");
  }

  try {
    const [network, tiers] = await Promise.all([
      networkAPI.getById(orgId, networkId),
      tierAPI.getAll(orgId, networkId),
    ]);
    return { network, tiers, organizationId: orgId };
  } catch (error) {
    if (error instanceof APIError && error.statusCode === 404) {
      throw redirect(`/organizations/${orgId}/networks`);
    }
    throw error;
  }
}

export async function networkMembershipsLoader({
  params,
}: LoaderFunctionArgs): Promise<NetworkMembershipsData> {
  const { id: orgId, networkId } = params;

  if (!orgId || !networkId || !isValidUUID(orgId) || !isValidUUID(networkId)) {
    throw redirect("/organizations");
  }

  try {
    const [network, tiers, memberships] = await Promise.all([
      networkAPI.getById(orgId, networkId),
      tierAPI.getAll(orgId, networkId),
      membershipAPI.getAll(orgId, networkId),
    ]);
    return { network, tiers, memberships, organizationId: orgId };
  } catch (error) {
    if (error instanceof APIError && error.statusCode === 404) {
      throw redirect(`/organizations/${orgId}/networks`);
    }
    throw error;
  }
}

export async function createNetworkLoader({
  params,
}: LoaderFunctionArgs): Promise<CreateNetworkLoaderData> {
  const { id: orgId } = params;

  if (!orgId || !isValidUUID(orgId)) {
    throw redirect("/organizations");
  }

  return { organizationId: orgId };
}

export interface NetworkAnalyticsData {
  network: NetworkResponse;
  tiers: NetworkTierResponse[];
  memberships: NetworkMembershipResponse[];
  revenueSummary: RevenueSummary;
  organizationId: string;
}

export async function networkAnalyticsLoader({
  params,
}: LoaderFunctionArgs): Promise<NetworkAnalyticsData> {
  const { id: orgId, networkId } = params;

  if (!orgId || !networkId || !isValidUUID(orgId) || !isValidUUID(networkId)) {
    throw redirect("/organizations");
  }

  try {
    const [network, tiers, memberships, revenueSummary] = await Promise.all([
      networkAPI.getById(orgId, networkId),
      tierAPI.getAll(orgId, networkId),
      membershipAPI.getAll(orgId, networkId),
      networkRevenueAPI.getSummary(orgId, networkId),
    ]);
    return { network, tiers, memberships, revenueSummary, organizationId: orgId };
  } catch (error) {
    if (error instanceof APIError && error.statusCode === 404) {
      throw redirect(`/organizations/${orgId}/networks`);
    }
    throw error;
  }
}
